import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Lock, Download, LogOut, Loader2, Users, Crown, GraduationCap, Briefcase } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "bs_admin_session";

const COLLECTIONS = [
  { key: "subscribers", label: "Subscribers", icon: <Users size={18} />, columns: ["created_at", "email", "source"] },
  { key: "sponsor_inquiries", label: "Sponsor Inquiries", icon: <Crown size={18} />, columns: ["created_at", "company", "contact_name", "email", "phone", "level", "message"] },
  { key: "applications", label: "Applications", icon: <GraduationCap size={18} />, columns: ["created_at", "full_name", "email", "phone", "city", "experience", "interest", "message"] },
  { key: "partner_inquiries", label: "Partner Inquiries", icon: <Briefcase size={18} />, columns: ["created_at", "organization", "contact_name", "email", "phone", "partnership_type", "message"] },
];

const AdminPage = () => {
  const [password, setPassword] = useState(sessionStorage.getItem(STORAGE_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [authChecking, setAuthChecking] = useState(false);
  const [authError, setAuthError] = useState("");
  const [counts, setCounts] = useState({});
  const [activeKey, setActiveKey] = useState("subscribers");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const auth = useCallback(async (pwd) => {
    setAuthChecking(true);
    setAuthError("");
    try {
      await axios.post(`${API}/admin/auth`, {}, { headers: { "x-admin-password": pwd } });
      sessionStorage.setItem(STORAGE_KEY, pwd);
      setAuthed(true);
      const s = await axios.get(`${API}/admin/summary`, { headers: { "x-admin-password": pwd } });
      setCounts(s.data?.counts || {});
    } catch (err) {
      setAuthError(err?.response?.data?.detail || "Authentication failed");
      setAuthed(false);
      sessionStorage.removeItem(STORAGE_KEY);
    } finally {
      setAuthChecking(false);
    }
  }, []);

  const loadRows = useCallback(async (key) => {
    setLoading(true);
    setActiveKey(key);
    try {
      const r = await axios.get(`${API}/admin/list/${key}`, { headers: { "x-admin-password": password } });
      setRows(r.data?.rows || []);
    } catch (e) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [password]);

  useEffect(() => {
    if (password) auth(password);
  }, [password, auth]);

  useEffect(() => {
    if (authed) loadRows(activeKey);
  }, [authed, activeKey, loadRows]);

  const exportCsv = async (key) => {
    try {
      const res = await axios.get(`${API}/admin/export/${key}`, {
        headers: { "x-admin-password": password },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `braid-summit-${key}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert("Export failed: " + (e?.response?.data?.detail || e.message));
    }
  };

  const logout = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    setPassword("");
    setAuthed(false);
    setCounts({});
    setRows([]);
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#0b0805] text-white flex items-center justify-center p-6">
        <form
          onSubmit={(e) => { e.preventDefault(); auth(password); }}
          className="w-full max-w-md bg-[#15110a] border border-[#D4AF37]/40 p-10"
          data-testid="admin-login"
        >
          <div className="flex items-center gap-3 mb-8">
            <Lock size={18} className="text-[#D4AF37]" />
            <span className="text-[#D4AF37] text-[10px] tracking-[0.4em] uppercase">Admin Access</span>
          </div>
          <h1 className="font-display text-3xl mb-3 leading-tight">The Braid Summit · Admin</h1>
          <p className="text-white/55 text-sm mb-8">Enter the admin password to view submissions.</p>
          <label className="block text-[10px] tracking-[0.3em] uppercase text-white/50 mb-2">Password</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoFocus
            required
            data-testid="admin-password-input"
            className="w-full bg-transparent border border-white/15 focus:border-[#D4AF37] outline-none px-4 py-3 text-sm text-white placeholder-white/30 transition-colors mb-2"
            placeholder="••••••••"
          />
          {authError && <p className="text-xs text-red-400 mt-2" data-testid="admin-error">{authError}</p>}
          <button
            type="submit"
            disabled={authChecking}
            data-testid="admin-login-submit"
            className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-black px-8 py-4 text-xs tracking-[0.2em] uppercase font-medium hover:bg-[#b8860b] disabled:opacity-60 transition-colors"
          >
            {authChecking ? <><Loader2 size={14} className="animate-spin" /> Signing in</> : "Sign In"}
          </button>
        </form>
      </div>
    );
  }

  const activeCol = COLLECTIONS.find((c) => c.key === activeKey);

  return (
    <div className="min-h-screen bg-[#0b0805] text-white">
      <header className="border-b border-white/10 px-6 md:px-10 py-5 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="font-display text-xl">The Braid Summit · Admin</div>
          <span className="hidden md:inline text-[10px] tracking-[0.3em] uppercase text-[#D4AF37]">Internal</span>
        </div>
        <button
          onClick={logout}
          data-testid="admin-logout"
          className="inline-flex items-center gap-2 text-white/60 hover:text-[#D4AF37] text-xs tracking-[0.2em] uppercase transition-colors"
        >
          <LogOut size={14} /> Logout
        </button>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-10">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 mb-10">
          {COLLECTIONS.map((c) => (
            <button
              key={c.key}
              onClick={() => loadRows(c.key)}
              data-testid={`admin-tab-${c.key}`}
              className={`text-left bg-[#15110a] p-6 transition-colors group ${activeKey === c.key ? "ring-1 ring-[#D4AF37]/60" : "hover:bg-[#1a150e]"}`}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="w-10 h-10 border border-[#D4AF37]/40 flex items-center justify-center text-[#D4AF37]">
                  {c.icon}
                </div>
                <span className="font-display text-3xl text-[#D4AF37]">{counts[c.key] ?? "—"}</span>
              </div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-white/50">{c.label}</div>
            </button>
          ))}
        </div>

        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
          <div>
            <h2 className="font-display text-2xl mb-1">{activeCol?.label}</h2>
            <p className="text-white/50 text-sm">{rows.length} record{rows.length === 1 ? "" : "s"} · most recent first</p>
          </div>
          <button
            onClick={() => exportCsv(activeKey)}
            data-testid={`admin-export-${activeKey}`}
            className="inline-flex items-center gap-2 bg-[#D4AF37] text-black px-6 py-3 text-xs tracking-[0.2em] uppercase font-medium hover:bg-[#b8860b] transition-colors"
          >
            <Download size={14} /> Export CSV
          </button>
        </div>

        <div className="border border-white/10 overflow-x-auto" data-testid="admin-table-wrapper">
          {loading ? (
            <div className="p-10 text-center text-white/50">
              <Loader2 size={20} className="animate-spin mx-auto mb-3" />
              Loading…
            </div>
          ) : rows.length === 0 ? (
            <div className="p-10 text-center text-white/50">No submissions yet.</div>
          ) : (
            <table className="w-full text-sm" data-testid="admin-table">
              <thead className="bg-[#15110a] border-b border-[#D4AF37]/30">
                <tr>
                  {activeCol.columns.map((col) => (
                    <th key={col} className="text-left text-[10px] tracking-[0.25em] uppercase text-[#D4AF37] py-3 px-4 font-medium">
                      {col.replace(/_/g, " ")}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr key={row.id || i} className={i % 2 === 0 ? "bg-[#0b0805]" : "bg-[#100c07]"}>
                    {activeCol.columns.map((col) => (
                      <td key={col} className="py-3 px-4 text-white/80 align-top max-w-xs truncate" title={String(row[col] || "")}>
                        {col === "created_at" && row[col]
                          ? new Date(row[col]).toLocaleString()
                          : col === "email" && row[col]
                          ? <a href={`mailto:${row[col]}`} className="text-[#D4AF37] hover:underline">{row[col]}</a>
                          : (row[col] || "—")}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPage;
